"use client";

import { Radio, RadioGroup } from "@headlessui/react";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { useFormContext, Controller, Path } from "react-hook-form";
import { ModalLabel } from "./ModalLabel";
import { cn } from "helpers/classNames";

export type RadioCardGroupItem = {
  id: string;
  label: string;
  customClassName?: string;
  checkmarkCustomClassName?: string;
  borderCheckedClassName?: string;
  isDisabled?: boolean;
};

type Props<T extends Record<string, any>> = {
  items: RadioCardGroupItem[];
  label: string;
  name: Path<T>;
  required?: boolean;
  className?: string;
  radioGroupClassName?: string;
};

export const RadioCardGroup = <T extends Record<string, any>>({
  items,
  label,
  name,
  required = false,
  className,
  radioGroupClassName,
}: Props<T>) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<T>();

  console.log({ items });

  return (
    <fieldset className={className}>
      <ModalLabel required={required}>
        {label}
        {/* {required && <span className="text-red-500 ml-0.5">*</span>} */}
      </ModalLabel>

      <Controller
        name={name}
        control={control}
        rules={{ required: required ? "This field is required" : false }}
        render={({ field: { onChange, value } }) => (
          <RadioGroup
            value={value}
            onChange={onChange}
            className={cn("mt-1 grid grid-cols-1 gap-y-4", radioGroupClassName)}
          >
            {items.map((item) => (
              <Radio
                key={item.id}
                value={item.id}
                aria-label={item.label}
                className={cn(
                  "group relative flex cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-2 shadow-sm hover:border-cyan-600 focus:outline-none data-[focus]:border-cyan-500 data-[focus]:ring-2 data-[focus]:ring-cyan-500",
                  !item.isDisabled && item.customClassName,
                  item.isDisabled && "cursor-not-allowed hover:bg-white",
                )}
                disabled={item.isDisabled}
              >
                <CheckCircleIcon
                  aria-hidden="true"
                  className={cn(
                    "size-5 text-cyan-500 group-[&:not([data-checked])]:hidden",
                    !item.isDisabled && item.checkmarkCustomClassName,
                  )}
                />
                <div
                  aria-hidden="true"
                  className={cn(
                    "hidden size-5 rounded-full border border-gray-300 group-[&:not([data-checked])]:block",
                    !item.isDisabled && item.checkmarkCustomClassName,
                  )}
                />
                <span
                  aria-hidden="true"
                  className={cn(
                    "pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-data-[focus]:border group-data-[checked]:border-cyan-500",
                    !item.isDisabled && item.borderCheckedClassName,
                  )}
                />
                <span
                  className={cn(
                    "z-10 ml-2 flex-1 text-sm font-medium text-gray-950 group-[&:not([data-checked])]:text-gray-700",
                    item.isDisabled &&
                      "text-gray-500 group-[&:not([data-checked])]:text-gray-500",
                  )}
                >
                  {item.label}
                </span>
              </Radio>
            ))}
          </RadioGroup>
        )}
      />

      {errors[name] && (
        <p className="mt-2 text-sm text-red-600">
          {errors[name]?.message as string}
        </p>
      )}
    </fieldset>
  );
};
